import { useState, useEffect } from "react";
import Navbar from "../../components/feature/Navbar";
import Footer from "../../components/feature/Footer";
import { fetchDownloads, type DownloadItem } from "../../api";

const fallbackDownloads = [
  {
    id: "catalog-2026",
    title: "Huatera Product Catalog 2026",
    description: "Complete product catalog featuring all two-way radios, accessories, and specifications.",
    fileType: "PDF",
    fileSize: "4.8 MB",
    icon: "ri-file-list-3-line",
    category: "Catalog",
  },
  {
    id: "ht900-datasheet",
    title: "HT-900 Pro Datasheet",
    description: "Detailed technical specifications, features, and operating parameters for the HT-900 Pro.",
    fileType: "PDF",
    fileSize: "1.2 MB",
    icon: "ri-file-text-line",
    category: "Datasheet",
  },
  {
    id: "ht950-datasheet",
    title: "HT-950 Ultra Datasheet",
    description: "Technical specifications and feature overview for the flagship HT-950 Ultra.",
    fileType: "PDF",
    fileSize: "1.4 MB",
    icon: "ri-file-text-line",
    category: "Datasheet",
  },
  {
    id: "ht700-datasheet",
    title: "HT-700 Rugged Datasheet",
    description: "Rugged series specifications including IP68 ratings and MIL-STD-810G compliance.",
    fileType: "PDF",
    fileSize: "1.1 MB",
    icon: "ri-file-text-line",
    category: "Datasheet",
  },
  {
    id: "user-manual",
    title: "Huatera Radio User Manual",
    description: "Comprehensive user guide covering setup, operation, programming, and maintenance.",
    fileType: "PDF",
    fileSize: "3.5 MB",
    icon: "ri-book-open-line",
    category: "Manual",
  },
  {
    id: "programming-guide",
    title: "Programming Software Guide",
    description: "Step-by-step guide for using the Huatera programming software and cable configuration.",
    fileType: "PDF",
    fileSize: "2.1 MB",
    icon: "ri-computer-line",
    category: "Manual",
  },
  {
    id: "accessories-guide",
    title: "Accessories Compatibility Guide",
    description: "Complete compatibility matrix for antennas, batteries, headsets, and chargers.",
    fileType: "PDF",
    fileSize: "1.8 MB",
    icon: "ri-plug-line",
    category: "Guide",
  },
  {
    id: "certification",
    title: "CE & FCC Certification Documents",
    description: "Official certification documents for CE, FCC, and RoHS compliance.",
    fileType: "PDF",
    fileSize: "5.2 MB",
    icon: "ri-shield-check-line",
    category: "Certificate",
  },
  {
    id: "oem-guide",
    title: "OEM / ODM Service Guide",
    description: "Overview of our customization services, branding options, and partnership programs.",
    fileType: "PDF",
    fileSize: "2.6 MB",
    icon: "ri-briefcase-line",
    category: "Guide",
  },
  {
    id: "firmware",
    title: "Latest Firmware Release Notes",
    description: "Firmware update history, changelog, and installation instructions for all models.",
    fileType: "PDF",
    fileSize: "890 KB",
    icon: "ri-refresh-line",
    category: "Firmware",
  },
  {
    id: "troubleshooting",
    title: "Troubleshooting & FAQ",
    description: "Common issues, solutions, and frequently asked questions for quick reference.",
    fileType: "PDF",
    fileSize: "1.5 MB",
    icon: "ri-question-line",
    category: "Guide",
  },
  {
    id: "warranty",
    title: "Warranty & Service Policy",
    description: "Detailed warranty terms, service procedures, and return policy information.",
    fileType: "PDF",
    fileSize: "980 KB",
    icon: "ri-file-shield-line",
    category: "Certificate",
  },
];

const fallbackCategories = ["All"];

export default function Downloads() {
  const [items, setItems] = useState<DownloadItem[]>(fallbackDownloads);
  const [categories, setCategories] = useState<string[]>(fallbackCategories);

  useEffect(() => {
    fetchDownloads()
      .then((res) => {
        if (res.items.length > 0) setItems(res.items);
        if (res.categories.length > 0) setCategories(["All", ...res.categories.map((c) => c.name)]);
      })
      .catch((err) => console.warn("下载数据加载失败:", err));
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <Navbar />
      <main>
        {/* Hero */}
        <section className="relative w-full h-[400px] md:h-[500px] overflow-hidden">
          <div className="absolute inset-0">
            <img
              src="https://readdy.ai/api/search-image?query=Professional%20technical%20documents%20and%20manuals%20stacked%20on%20modern%20desk%20with%20tablet%20and%20radio%20equipment%20warm%20lighting%20clean%20workspace%20high%20quality%20product%20photography%20overhead%20view%20technology%20download%20center%20theme&width=1400&height=800&seq=200&orientation=landscape"
              alt="Downloads"
              className="w-full h-full object-cover object-top"
            />
            <div className="absolute inset-0 bg-gradient-to-r from-dark/60 to-dark/30"></div>
          </div>
          <div className="relative z-10 h-full flex flex-col justify-center px-4 md:px-8 lg:px-12">
            <div className="max-w-4xl">
              <div className="inline-flex items-center px-4 py-1.5 bg-white/20 rounded-full mb-6">
                <span className="text-white text-sm font-medium">Resources</span>
              </div>
              <h1 className="text-4xl md:text-6xl font-bold text-white leading-tight">
                Download<br />
                <span className="text-secondary">Center</span>
              </h1>
              <p className="text-white/80 text-lg mt-4 max-w-2xl">
                Access product catalogs, datasheets, user manuals, certifications, and more.
              </p>
            </div>
          </div>
        </section>

        {/* Download List */}
        <section className="py-12 md:py-16 px-4 md:px-8 lg:px-12">
          <div className="max-w-6xl mx-auto">
            {/* Category Filter */}
            <div className="flex flex-wrap items-center gap-3 mb-10">
              <span className="text-gray-500 text-sm mr-2">Filter:</span>
              {categories.map((cat) => (
                <button
                  key={cat}
                  className="px-4 py-2 rounded-full text-sm font-medium transition-colors whitespace-nowrap bg-white text-gray-700 hover:bg-gray-100 border border-gray-200"
                >
                  {cat}
                </button>
              ))}
            </div>

            {/* Download Grid */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {items.map((item) => (
                <div
                  key={item.id}
                  className="group bg-white rounded-xl border border-gray-100 hover:border-primary/30 transition-all duration-300 overflow-hidden"
                >
                  <div className="p-6">
                    <div className="flex items-start justify-between mb-4">
                      <div className="w-12 h-12 rounded-lg bg-primary/10 flex items-center justify-center flex-shrink-0">
                        <i className={`${item.icon} text-primary text-xl`}></i>
                      </div>
                      <span className="inline-flex items-center px-2.5 py-1 bg-light-gray text-gray-500 text-xs font-medium rounded-full">
                        {item.fileType}
                      </span>
                    </div>
                    <h3 className="text-dark font-bold text-base mb-2 leading-tight">
                      {item.title}
                    </h3>
                    <p className="text-gray-500 text-sm leading-relaxed mb-4">
                      {item.description}
                    </p>
                    <div className="flex items-center justify-between pt-4 border-t border-gray-100">
                      <span className="text-gray-400 text-xs">{item.fileSize}</span>
                      <button
                        className="inline-flex items-center gap-2 px-4 py-2 bg-primary text-white text-sm font-medium rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
                      >
                        <i className="ri-download-line"></i>
                        Download
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </section>

        {/* Contact CTA */}
        <section className="bg-light-gray py-16 md:py-24 px-4 md:px-8 lg:px-12">
          <div className="max-w-4xl mx-auto text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-dark mb-4">
              Need a Specific Document?
            </h2>
            <p className="text-gray-600 mb-8 max-w-xl mx-auto">
              Can&apos;t find what you are looking for? Contact our team and we will provide the resources you need.
            </p>
            <a
              href="/contact"
              className="inline-flex items-center gap-3 px-8 py-3 bg-primary text-white font-semibold rounded-full hover:bg-primary-dark transition-colors whitespace-nowrap"
            >
              Contact Us
              <i className="ri-arrow-right-line"></i>
            </a>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
}